import React, { useEffect, useState } from 'react';
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box, IconButton, Modal, TextField, InputAdornment, Typography, Button,
} from '@mui/material';
import Paginations from '../../common/pagination/Paginations';
import { acceptProductApi, allPendingProductsApi, rejectProductApi } from '../../api';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import UserImg from "../../common/assests/sampleuser.svg";
import { toast } from 'react-toastify';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import configuration from '../../config/Config';
import CloseIcon from "@mui/icons-material/Close";
import { AdminTable, ActionCell } from '../../common/adminTable';

const NewAddedProduct = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const { id } = useParams();
  const [selectedRow, setSelectedRow] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const handleRejectModalOpen = () => setRejectModalOpen(true);
  const handleRejectModalClose = () => setRejectModalOpen(false);
  const [pendingProductData, setPendingProductData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [change, setChange] = useState(false);
  const [myTimout, setMyTimeOut] = useState();
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);

  const handleNewProduct = async () => {
    try {
      setLoading(true);
      const response = await allPendingProductsApi({ token, currentPage, searchQuery, id });
      setPendingProductData(response?.products || []);
      setCurrentPage(response?.currentPage);
      setTotalPages(response?.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleRejectProduct = async (id) => {
    if (!reason) {
      toast.warning("Reason Required");
      return;
    }
    const isConfirmed = await confirm({ title: "Reject Product", description: "Are you sure you want to reject this product?", confirmText: "Reject", destructive: true });
    if (!isConfirmed) return;
    try {
      const status = "rejected";
      const response = await rejectProductApi({ token, id, status, reject_reason: reason });
      setReason("");
      setChange(!change);
      toast.success(response.message);
      setOpen(false);
    } catch (error) {
      toast.error(error.message);
      console.log("error", error);
    }
  };

  const handleAcceptProduct = async (id) => {
    const isConfirmed = await confirm({ title: "Accept Product", description: "Are you sure you want to accept this product?", confirmText: "Accept" });
    if (!isConfirmed) return;
    try {
      const status = "active";
      const response = await acceptProductApi({ token, id, status });
      setChange(!change);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message);
      console.log("error", error);
    }
  };

  useEffect(() => {
    handleNewProduct();
  }, [currentPage, change]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      handleNewProduct();
    } else {
      clearTimeout(myTimout);
      const timeout = setTimeout(() => handleNewProduct(), 500);
      setMyTimeOut(timeout);
    }
  }, [searchQuery]);

  const handlePageChange = (page) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const handleClickOpen = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const columns = [
    {
      field: "photo",
      headerName: "Image",
      width: 70,
      renderCell: (row) => (
        <img
          src={row?.photos?.length > 0 ? `${configuration.cdnFront}/${row.photos[0].url}` : UserImg}
          alt="product"
          style={{ height: 40, width: 40, borderRadius: "6px", objectFit: "cover" }}
        />
      ),
    },
    { field: "title", headerName: "Name", minWidth: 130, renderCell: (r) => r?.title || "-" },
    { field: "category", headerName: "Category", width: 120, renderCell: (r) => r?.categoryId?.name || "-" },
    { field: "price", headerName: "Price", width: 90, renderCell: (r) => `$${r?.price || "-"}` },
    { field: "discountedProce", headerName: "Discounted Price", width: 130, renderCell: (r) => `$${r?.discountedProce || "-"}` },
    {
      field: "updated_at",
      headerName: "Date Submitted",
      width: 130,
      renderCell: (r) => r?.updated_at ? new Date(r.updated_at).toLocaleDateString() : "-",
    },
    { field: "status", headerName: "Status", width: 90, renderCell: (r) => r?.status || "-" },
    {
      field: "__actions",
      headerName: "",
      width: 120,
      align: "right",
      renderCell: (row) => (
        <ActionCell
          actions={[
            {
              label: "View",
              icon: <VisibilityIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleClickOpen(row),
            },
            {
              label: "Accept",
              icon: <CheckCircleOutlineIcon fontSize="small" />,
              color: "#065F46",
              onClick: () => handleAcceptProduct(row?._id),
            },
            {
              label: "Reject",
              icon: <CancelIcon fontSize="small" />,
              color: "#EA001B",
              onClick: () => { setSelectedRow(row); handleRejectModalOpen(); },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <Box
        sx={{
          marginTop: "40px",
          marginBottom: "30px",
          padding: "26px",
          borderRadius: "24px",
          boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)",
          border: "1px solid #F4F4F4",
          bgcolor: "#FFF",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", gap: { xs: "15px" } }}>
          <Typography sx={{ fontSize: { xs: '16px', sm: '20px' }, fontWeight: "600", color: "#362B24" }}>
            Product List
          </Typography>
          <TextField
            placeholder="Search by name"
            value={searchQuery}
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setSearchQuery(e.target.value);
              }
            }}
            size="small"
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                background: "#fff",
                fontFamily: "Heebo",
                "& fieldset": { borderColor: "#E7E7E7" },
                "&:hover fieldset": { borderColor: "#D7D7D7" },
                "&.Mui-focused fieldset": { borderColor: "#065F46" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#8B8B8B", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <AdminTable
          columns={columns}
          rows={pendingProductData}
          loading={loading}
          rowKey="_id"
          emptyText="No products found"
        />
        <Paginations
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          disabled={loading}
        />
      </Box>

      {/* Product Detail Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            maxWidth: '630px',
            width: '90%',
            maxHeight: '80vh',
            bgcolor: '#F5F5F5',
            borderRadius: '24px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: 24,
            p: '20px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: '700' }}>Product Details</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {selectedRow && (
            <Box sx={{ marginTop: "20px" }}>
              <img
                src={selectedRow?.photos?.length > 0 ? `${configuration.cdnFront}/${selectedRow.photos[0].url}` : UserImg}
                alt="product"
                style={{ width: '100%', height: 'auto', maxHeight: '231px', borderRadius: '24px', marginBottom: '20px' }}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 0fr))", gap: "5px" }}>
                {selectedRow?.photos?.map((image) => (
                  <Box
                    key={image?._id}
                    sx={{
                      width: "77px",
                      height: "79px",
                      flexShrink: 0,
                      borderRadius: "16px",
                      boxShadow: "3px 2px 20px 0px rgba(0, 0, 0, 0.04)",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={image?.url ? `${configuration.cdnFront}/${image.url}` : UserImg}
                      alt="img"
                      style={{ width: "100%", height: "100%", maxHeight: "80px", objectFit: "cover" }}
                    />
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 0fr))", gap: 3, marginTop: '25px' }}>
                <Box>
                  <Typography sx={{ fontWeight: "400", fontSize: '13px', opacity: "0.6", color: "#362B24" }}>Product Name</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{selectedRow?.title || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", fontSize: '13px', opacity: "0.6", color: "#362B24" }}>Product Category</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{selectedRow?.categoryId?.name || "-"}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", opacity: "0.6", color: "#362B24", fontSize: "15px" }}>Price</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{`$${selectedRow?.price || "-"}`}</strong></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: "400", fontSize: '13px', opacity: "0.6", color: "#362B24" }}>Discount Price</Typography>
                  <Typography sx={{ fontWeight: "500", fontSize: "16px" }}><strong>{`$${selectedRow?.discountedProce || "-"}`}</strong></Typography>
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: '16px', color: "#362B24", marginTop: '20px' }}>Description</Typography>
                <Typography sx={{ fontSize: "13px", opacity: "0.5", color: "#362B24", fontWeight: '400', lineHeight: '24px' }}>
                  {selectedRow?.description || "N/A"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", mt: 3, mb: 1, flexWrap: 'wrap' }}>
                <Button
                  onClick={() => handleAcceptProduct(selectedRow?._id)}
                  sx={{
                    width: "180px", height: "50px", borderRadius: "15px",
                    backgroundColor: "#065F46", color: "#FFF", textTransform: "none",
                    "&:hover": { backgroundColor: "#065F46" }, flexShrink: 0,
                  }}
                >
                  Accept
                </Button>
                <Button
                  onClick={() => handleRejectModalOpen()}
                  sx={{
                    width: "180px", height: "50px", borderRadius: "15px",
                    backgroundColor: "#EA001B", color: "#FFF", textTransform: "none",
                    "&:hover": { backgroundColor: "#EA001B" }, flexShrink: 0,
                  }}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Reject Reason Modal */}
      <Modal open={rejectModalOpen} onClose={handleRejectModalClose}>
        <Box
          sx={{
            maxWidth: '538px',
            width: '90%',
            height: '418px',
            borderRadius: '32px',
            backgroundColor: '#FFF',
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ color: '#362B24', fontSize: '20px', fontWeight: '700' }}>Reason for Rejection</Typography>
            <IconButton onClick={handleRejectModalClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={{ color: '#362B24', marginTop: '15px', fontSize: '13px', fontWeight: '600' }}>
            Type your Reason
          </Typography>
          <textarea
            placeholder="Type here"
            onChange={(e) => {
              if (e.target.value.trim() !== "" || e.target.value === "") {
                setReason(e.target.value);
              }
            }}
            style={{
              maxWidth: '470px',
              width: '100%',
              height: '202px',
              borderRadius: '18px',
              border: '1.4px solid #362B24',
              outline: 'none',
              backgroundColor: '#FFF',
              padding: '16px',
              fontSize: '16px',
              resize: 'none',
              marginTop: '15px',
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              onClick={() => {
                handleRejectProduct(selectedRow?._id);
                handleRejectModalClose();
              }}
              sx={{
                width: "196px", height: "50px", borderRadius: "15px",
                backgroundColor: "#EA001B", color: "#FFF", textTransform: "none",
                "&:hover": { backgroundColor: "#EA001B" }, flexShrink: 0,
              }}
            >
              Reject
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default NewAddedProduct;
